import { useEffect, useState, useRef } from "react";
import { generateClient } from "aws-amplify/api";
import { listSessionAnalyses } from "../graphql/queries";
import SessionResultView from "./SessionResultView";
import PrintSessionResultView from "./PrintSessionResultView";

const client = generateClient();

// Normalize MRN/ID across legacy and new keys
const getIdentifiers = (obj = {}) => {
  const mrn = obj.patientMRN ?? obj.patientMrn ?? obj.mrn ?? null;
  const pid = obj.patientId ?? obj.id ?? null;
  return { mrn, pid };
};

export default function SessionBrowser({ userId }) {
  const [sessions, setSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState("date");
  const printContainerRef = useRef();

  const loadUserSessions = async () => {
    try {
      const res = await client.graphql({
        query: listSessionAnalyses,
        variables: {
          filter: { userId: { eq: userId } },
          limit: 100,
        },
      });

      const parsed = res.data.listSessionAnalyses.items.map((item) => {
        let parsedResult = {};
        try {
          parsedResult = JSON.parse(item.analysisResult || "{}");
        } catch (e) {
          console.warn("Could not parse analysisResult for session", item.sessionId);
        }
        return { ...item, parsedResult };
      });

      setSessions(parsed);
      setFilteredSessions(parsed);
    } catch (err) {
      console.error("❌ Failed to load user sessions:", err);
    }
  };

  useEffect(() => {
    if (userId) {
      loadUserSessions();
    }
  }, [userId]);

  useEffect(() => {
    const loweredSearch = (searchTerm || "").toLowerCase();

    const filtered = sessions.filter((s) => {
      const name = (s.parsedResult.name || "").toLowerCase();
      const { mrn, pid } = getIdentifiers(s.parsedResult);
      const mrnStr = (mrn != null ? String(mrn) : "").toLowerCase();
      const pidStr = (pid != null ? String(pid) : "").toLowerCase();
      return (
        name.includes(loweredSearch) ||
        mrnStr.includes(loweredSearch) ||
        pidStr.includes(loweredSearch)
      );
    });

    const sorted = [...filtered].sort((a, b) => {
      if (sortKey === "date") {
        return new Date(b.createdAt) - new Date(a.createdAt);
      } else if (sortKey === "name") {
        return (a.parsedResult.name || "").localeCompare(b.parsedResult.name || "");
      }
      return 0;
    });

    setFilteredSessions(sorted);
  }, [searchTerm, sortKey, sessions]);

  const toggleSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const handleBatchPrint = () => {
    const content = printContainerRef.current?.innerHTML;
    if (!content) return;

    const printWindow = window.open("", "", "width=800,height=600");
    printWindow.document.write(`
      <html>
        <head>
          <title>Batch Print</title>
          <style>
            body { font-family: sans-serif; padding: 2rem; }
            .section { margin-bottom: 4rem; }
          </style>
        </head>
        <body>${content}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="session-browser">
      <h2 className="text-xl font-bold mb-4">Your Sessions</h2>

      <div className="search-controls">
        <input
          type="text"
          placeholder="Search by name or MRN/ID"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
          <option value="date">Sort by Date</option>
          <option value="name">Sort by Name</option>
        </select>
        <button
          onClick={loadUserSessions}
          className="bg-blue-500 text-white px-4 py-1 rounded hover:bg-blue-600"
        >
          Refresh
        </button>
        {selectedIds.length > 0 && (
          <button onClick={handleBatchPrint} className="button print">
            Print {selectedIds.length} Selected
          </button>
        )}
      </div>

      {filteredSessions.length === 0 && (
        <p className="text-muted">No sessions found.</p>
      )}

      <ul
        className="session-list"
        style={{
          maxHeight: '400px',
          overflowY: 'auto',
          border: '1px solid #ccc',
          borderRadius: '8px',
          padding: '0.5rem',
          marginTop: '1rem'
        }}
      >
        {filteredSessions.map((session) => {
          const { mrn, pid } = getIdentifiers(session.parsedResult);
          const rightHand = (mrn && `MRN ${mrn}`) || (pid && `ID ${pid}`) || "No MRN/ID";
          return (
            <li key={session.id} className="session-item">
              <div className="session-content">
                <div className="session-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(session.id)}
                    onChange={() => toggleSelection(session.id)}
                  />
                </div>
                <div
                  onClick={() => setSelectedId(session.id)}
                  className="session-link"
                  style={{ cursor: 'pointer' }}
                >
                  {session.parsedResult.name || "No Name"} — {rightHand}
                  <span className="text-muted">
                    ({new Date(session.createdAt).toLocaleString()})
                  </span>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {selectedId && (
        <div className="mt-6">
          <SessionResultView
            sessionId={selectedId}
            createdAt={
              sessions.find((s) => s.id === selectedId)?.createdAt || null
            }
          />
        </div>
      )}

      <div ref={printContainerRef} style={{ display: "none" }}>
        {selectedIds.map((id) => {
          const session = sessions.find((s) => s.id === id);
          return (
            <div className="section" key={id}>
              <PrintSessionResultView
                sessionId={id}
                createdAt={session?.createdAt || null}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}



